import { forwardRef, useMemo } from 'react';
import { NavLink, useMatches } from 'react-router';
import { Group, Menu, Stack, UnstyledButton } from '@mantine/core';
import { ChevronDownIcon, ExternalLink } from 'lucide-react';
import clsx from 'clsx';

import * as classes from './header-navigation.css';
import { NavItem, NavSubItem } from './types';

export function Navigation({ navItems }: { navItems: NavItem[] }) {
  if (!navItems) {
    return null;
  }

  return (
    <Group gap="0.125rem">
      {navItems.map((item) => {
        if (item.subItems) {
          return (
            <Menu
              key={item.id}
              trigger="click-hover"
              withArrow
              shadow="md"
              offset={4}
              loop={false}
              withinPortal={false}
              trapFocus={false}
              menuItemTabIndex={0}
              transitionProps={{
                transition: {
                  in: {
                    opacity: 1,
                    transform: 'rotateX(0deg) scale(1)',
                  },
                  out: {
                    opacity: 0,
                    transform: 'rotateX(-30deg) scale(0.9)',
                  },
                  common: {
                    transformOrigin: 'top center',
                  },
                  transitionProperty: 'transform, opacity',
                },
                duration: 200,
                timingFunction: 'ease',
              }}
            >
              <NavigationMenuTarget {...item} />
              <Menu.Dropdown miw={175}>
                <Stack gap="0.125rem">
                  {item.subItems.map((subItem) => (
                    <NavigationSubItem key={subItem.id} item={subItem} />
                  ))}
                </Stack>
              </Menu.Dropdown>
            </Menu>
          );
        }

        return <NavigationLink key={item.id} {...item} />;
      })}
    </Group>
  );
}

function NavigationSubItem({ item }: { item: NavSubItem }) {
  const { href, to, label, Icon, target } = item;
  if (href) {
    return (
      <Menu.Item
        component="a"
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        leftSection={Icon && <Icon size={14} />}
        rightSection={target === '_blank' && <ExternalLink size={14} />}
      >
        {label}
      </Menu.Item>
    );
  }

  if (item.to) {
    return (
      <Menu.Item
        leftSection={Icon && <Icon size={14} />}
        renderRoot={({ className, ...props }) => (
          <NavLink
            to={to}
            className={({ isActive }) => clsx(className, { [classes.mainNavLinkActive]: isActive })}
            {...props}
          />
        )}
      >
        {item.label}
      </Menu.Item>
    );
  }

  return (
    <Menu.Item leftSection={Icon && <Icon size={14} />} disabled>
      {label}
    </Menu.Item>
  );
}

const NavigationLink = forwardRef<HTMLButtonElement, NavItem>((item, ref) => {
  const { id, href, to, target } = item;

  if (to) {
    return (
      <NavLink
        id={id}
        className={({ isActive }) =>
          clsx(classes.mainNavLink, { [classes.mainNavLinkActive]: isActive })
        }
        to={to}
      >
        <NavigationLinkContent {...item} />
      </NavLink>
    );
  }

  if (href) {
    return (
      <a
        id={id}
        className={classes.mainNavLink}
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      >
        <NavigationLinkContent {...item} />
      </a>
    );
  }

  return null;
});

const NavigationMenuTarget = (item: NavItem) => {
  const { id, subItems } = item;
  const subItemsTo = subItems?.map((item) => item.to || '');
  const parentIsActive = useParentIsActive(subItemsTo || []);

  return (
    <Menu.Target>
      <UnstyledButton
        id={id}
        className={clsx(classes.mainNavLink, { [classes.mainNavLinkActive]: parentIsActive })}
        type="button"
      >
        <NavigationLinkContent {...item} />
      </UnstyledButton>
    </Menu.Target>
  );
};

const NavigationLinkContent = ({ label, subItems }: NavItem) => {
  return (
    <Group gap="0.5rem">
      {label}
      {subItems && <ChevronDownIcon size={14} aria-hidden />}
    </Group>
  );
};

function useParentIsActive(subItemsTo: string[]) {
  const matches = useMatches();
  const matchedPaths = useMemo(() => matches.map((match) => match.pathname), [matches]);

  return useMemo(
    () => matchedPaths.some((path) => subItemsTo.includes(path)),
    [matchedPaths, subItemsTo],
  );
}
